import { ActionHintDirective } from '@/app/shared/directives/action-hint.directive';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { LayoutService } from '@/app/layout/service/layout.service';
import { AuthService } from '@/app/core/auth/auth.service';
import { ContextoTenantStore } from '@/app/core/auth/contexto-tenant.store';
import { obtenerRutaAvatar } from '@/app/core/auth/avatar.constants';
import { LoadingService } from '@/app/core/loading/loading.service';
import { AvatarSelectorComponent } from './avatar-selector.component';

@Component({
    selector: '[app-profilesidebar]',
    imports: [ActionHintDirective, ButtonModule, DrawerModule, AvatarSelectorComponent],
    template: `
        <app-avatar-selector #selectorAvatar />
        <p-drawer
            header="Mi cuenta"
            ariaCloseLabel="Cerrar perfil"
            [visible]="visible()"
            (onHide)="onDrawerHide()"
            [closeOnEscape]="!selectorAvatar.visible()"
            position="right"
            [transitionOptions]="'.3s cubic-bezier(0, 0, 0.2, 1)'"
            [style]="{ width: 'min(100vw, 24rem)' }"
            styleClass="layout-profile-sidebar"
        >
            <div class="flex h-full flex-col">
                <div class="flex flex-col items-center px-2 pb-6 pt-4 text-center">
                    <div class="relative mb-6 inline-flex">
                        <img [src]="avatarRuta()" alt="Tu avatar" width="112" height="112" class="rounded-full" />
                        <p-button ariaLabel="Cambiar avatar" icon="pi pi-pencil" [rounded]="true" size="small" [style]="{ width: '2rem', height: '2rem' }" class="absolute bottom-0 -right-1" (onClick)="selectorAvatar.abrir()"  appActionHint="Cambiar avatar" />
                    </div>
                    <p class="mb-1 mt-0 text-sm text-surface-500">Bienvenido</p>
                    <h2 class="m-0 max-w-full break-words text-xl font-semibold">{{ nombreVisible() }}</h2>
                    @if (correo()) {
                        <p class="mb-0 mt-2 max-w-full break-all text-sm text-surface-500">{{ correo() }}</p>
                    }
                    @if (contexto.administradorplataforma()) {
                        <span class="mt-4 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-950 dark:text-primary-200">Administrador de plataforma</span>
                    }
                </div>
                <div class="mt-auto border-t border-surface-200 pt-5 dark:border-surface-700">
                    <p-button label="Cerrar sesión" ariaLabel="Cerrar sesión" icon="pi pi-sign-out" severity="secondary" [outlined]="true" styleClass="w-full" [loading]="signingOut()" [disabled]="signingOut()" (onClick)="signOut()"  appActionHint="Cerrar sesión" />
                </div>
            </div>
        </p-drawer>
    `
})
export class AppProfileSidebar {
    readonly layoutService = inject(LayoutService);
    readonly auth = inject(AuthService);
    readonly contexto = inject(ContextoTenantStore);
    private readonly globalLoading = inject(LoadingService);
    private readonly router = inject(Router);
    readonly signingOut = signal(false);
    readonly visible = computed(() => !!this.layoutService.layoutState().profileSidebarVisible);
    readonly avatarRuta = computed(() => obtenerRutaAvatar(this.contexto.sesion()?.avatar));
    readonly correo = computed(() => this.contexto.sesion()?.correoelectronico || this.auth.user()?.email || '');
    readonly nombreVisible = computed(() => this.contexto.sesion()?.nombrevisible || this.auth.user()?.username || 'Mi cuenta');

    onDrawerHide(): void {
        this.layoutService.layoutState.update((state) => ({ ...state, profileSidebarVisible: false }));
    }

    async signOut(): Promise<void> {
        if (this.signingOut()) return;
        this.signingOut.set(true);
        try {
            await this.globalLoading.trackPromise(this.auth.signOut(), 'blocking');
            this.onDrawerHide();
            await this.router.navigate(['/auth/login']);
        } finally {
            this.signingOut.set(false);
        }
    }
}
